/**
 * ReplayTimeline - Film strip sidebar for navigating replay steps
 * 
 * Each trick shows miniature cards in NSEW layout (E/W overlap N/S for z-index visibility).
 * Uses MiniCard for rendering (zoomed top-left for faces/trumps, number+suit for pips).
 */

import { useEffect, useRef, useMemo } from 'react';
import MiniCard, { MiniCardBack } from './MiniCard';
import type { GameHistory, ReplayStep } from '../game/gameHistory';

type ScreenPosition = 'bottom' | 'top' | 'left' | 'right' | 'top-left' | 'top-right';

interface ReplayTimelineProps {
  history: GameHistory;
  steps: ReplayStep[];
  activeStepIndex: number;
  onStepClick: (index: number) => void;
}

function getScreenPositions(playerCount: number, myPosition: number): Map<number, ScreenPosition> {
  const layouts: Record<number, ScreenPosition[]> = {
    3: ['bottom', 'right', 'left'],
    4: ['bottom', 'right', 'top', 'left'],
    5: ['bottom', 'right', 'top-right', 'top-left', 'left'],
  };
  const layout = layouts[playerCount] || layouts[4];
  const positions = new Map<number, ScreenPosition>();
  for (let i = 0; i < playerCount; i++) {
    const relativePos = (i - myPosition + playerCount) % playerCount;
    positions.set(i, layout[relativePos] || 'bottom');
  }
  return positions;
}

function getThumbnailClass(pos: ScreenPosition): string {
  switch (pos) {
    case 'bottom': return 'thumb-south';
    case 'top': return 'thumb-north';
    case 'left': return 'thumb-west';
    case 'right': return 'thumb-east';
    case 'top-left': return 'thumb-north-west';
    case 'top-right': return 'thumb-north-east';
    default: return 'thumb-south';
  }
}

export default function ReplayTimeline({ history, steps, activeStepIndex, onStepClick }: ReplayTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  
  const screenPositions = useMemo(
    () => getScreenPositions(history.playerCount, history.myPosition),
    [history.playerCount, history.myPosition]
  );
  
  // Auto-scroll to vertically center the active item
  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const item = activeRef.current;
      const containerRect = container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.top + itemRect.height / 2;
      const containerCenter = containerRect.top + containerRect.height / 2;
      container.scrollBy({ top: itemCenter - containerCenter, behavior: 'smooth' });
    }
  }, [activeStepIndex]);
  
  return (
    <div className="replay-timeline">
      <div className="replay-timeline-scroll" ref={scrollRef}>
        {steps.map((step, index) => {
          const isActive = index === activeStepIndex;
          return (
            <div
              key={`${step.type}-${step.trickNumber ?? 0}`}
              ref={isActive ? activeRef : undefined}
              className={`replay-timeline-item ${isActive ? 'active' : ''}`}
              onClick={() => onStepClick(index)}
            >
              <span className="replay-timeline-item-label">{step.label}</span>
              {step.type === 'trick' && step.trickNumber !== undefined ? (
                <TrickThumbnail
                  history={history}
                  trickNumber={step.trickNumber}
                  screenPositions={screenPositions}
                />
              ) : (
                <SpecialThumbnail type={step.type} history={history} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// Trick Thumbnail
// ============================================

function TrickThumbnail({ history, trickNumber, screenPositions }: {
  history: GameHistory;
  trickNumber: number;
  screenPositions: Map<number, ScreenPosition>;
}) {
  const trick = history.tricks.find(t => t.trickNumber === trickNumber);
  if (!trick) return null;
  
  return (
    <div className="replay-timeline-thumbnail">
      {trick.cards.map((play, idx) => {
        const screenPos = screenPositions.get(play.player) || 'bottom';
        const thumbClass = getThumbnailClass(screenPos);
        return (
          <MiniCard
            key={`${play.card}-${idx}`}
            card={play.card}
            className={thumbClass}
            style={{ zIndex: idx + 1 }}
          />
        );
      })}
    </div>
  );
}

// ============================================
// Special Thumbnails (distrib, chien, ecart)
// ============================================

function SpecialThumbnail({ type, history }: { type: string; history: GameHistory }) {
  switch (type) {
    case 'distribution':
      return (
        <div className="replay-timeline-special">
          <MiniCardBack />
          <MiniCardBack />
          <MiniCardBack />
        </div>
      );
    case 'chien':
      return (
        <div className="replay-timeline-special">
          {history.chien.map((card) => (
            <MiniCard key={card} card={card} />
          ))}
        </div>
      );
    case 'ecart':
      return (
        <div className="replay-timeline-special">
          {history.ecart.map((card) => (
            <MiniCard key={card} card={card} />
          ))}
        </div>
      );
    default:
      return null;
  }
}
